import React, { useEffect, useState } from 'react';
import { Table, Button, Input, Space, Tag, App, Typography, Select, Modal } from 'antd';
import { ReloadOutlined, RollbackOutlined } from '@ant-design/icons';
import api from '@/api/client';
import type { PaginatedResponse } from '@/types';
import dayjs from 'dayjs';

const { Title } = Typography;

const statusMap: Record<string, { color: string; label: string }> = {
  paid: { color: 'green', label: '已支付' },
  pending: { color: 'orange', label: '待支付' },
  cancelled: { color: 'default', label: '已取消' },
  refunded: { color: 'red', label: '已退款' },
};

export default function OrdersPage() {
  const [data, setData] = useState<PaginatedResponse<any>>({ items: [], total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>();
  const { message } = App.useApp();

  const loadOrders = async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: 20 };
      if (status) params.status = status;
      const { data: res } = await api.get('/orders', { params });
      setData(res);
    } catch {
      message.error('加载订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrders(); }, [status]);

  const handleRefund = (record: any) => {
    Modal.confirm({
      title: '确认退款',
      content: `订单号 ${record.order_no || record.id}，金额 ${record.amount} 元`,
      okText: '确认退款',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.post(`/orders/${record.id}/refund`);
          message.success('退款成功');
          loadOrders(data.page);
        } catch { message.error('退款失败'); }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '订单号', dataIndex: 'order_no', width: 160 },
    { title: '用户名', dataIndex: 'username', width: 100 },
    { title: '金额', dataIndex: 'amount', width: 80, render: (v: number) => `¥${v}` },
    { title: '积分', dataIndex: 'credits', width: 60 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (s: string) => <Tag color={statusMap[s]?.color}>{statusMap[s]?.label || s}</Tag>,
    },
    {
      title: '时间', dataIndex: 'created_at', width: 120,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作', width: 100,
      render: (_: any, record: any) => (
        record.status === 'paid' ? (
          <Button size="small" danger icon={<RollbackOutlined />} onClick={() => handleRefund(record)}>退款</Button>
        ) : null
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>订单管理</Title>
      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="按状态筛选" allowClear style={{ width: 120 }}
          value={status} onChange={(v) => setStatus(v)}
          options={[
            { label: '待支付', value: 'pending' },
            { label: '已支付', value: 'paid' },
            { label: '已取消', value: 'cancelled' },
            { label: '已退款', value: 'refunded' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadOrders()}>刷新</Button>
      </Space>
      <Table
        columns={columns} dataSource={data.items} rowKey="id" loading={loading}
        pagination={{ current: data.page, pageSize: data.pageSize, total: data.total, onChange: loadOrders, showSizeChanger: false }}
      />
    </div>
  );
}
